/**
 * Extract a human-readable error message from any axios/fetch error shape.
 *
 * FastAPI returns several shapes:
 *   - simple HTTPException → { detail: "string" }
 *   - validation error     → { detail: [{loc, msg, type}, ...] }
 *   - 500 / proxy gateway  → no body, or plain text, or {error: "..."}
 *
 * This helper drains all of them so toasts never lie to the user with
 * a generic "Signup failed".
 */
export function extractApiError(err, fallback = "Something went wrong") {
  if (!err) return fallback;
  // Axios style
  const data = err.response?.data;
  if (data) {
    if (typeof data === "string" && data.trim()) return data;
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail) && data.detail.length > 0) {
      // FastAPI validation errors → join field messages
      return data.detail
        .map((d) => (d?.msg ? `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg}` : null))
        .filter(Boolean)
        .join(", ") || fallback;
    }
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
    try {
      const s = JSON.stringify(data);
      if (s && s !== "{}") return s;
    } catch { /* ignore */ }
  }
  if (typeof err.message === "string" && err.message) return err.message;
  return fallback;
}

export const friendlyApiError = extractApiError;
